import React from 'react';
import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const ComponentCarousel = ({ 
  components,
  height = '400px',
  width = '100%',
  maxWidth = '100%',
  autoAnimate = false,
  autoAnimateTimeMs = 5000,
}) => {

  const [currentIndex, setCurrentIndex] = useState(0);
  const [dragStart, setDragStart] = useState(0);
  const [direction, setDirection] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef(null);
  const autoAnimateTimerRef = useRef(null);

  const goToNextSlide = useCallback(() => {
    setDirection(currentIndex === components.length - 1 ? -1 : 1);
    setCurrentIndex(current => 
      current === components.length - 1 ? 0 : current + 1
    );
  }, [components.length, currentIndex]);

  useEffect(() => {
    if (autoAnimate) {
      // Clear any existing timer
      if (autoAnimateTimerRef.current) {
        clearTimeout(autoAnimateTimerRef.current);
      }
      
      // Set new timer
      autoAnimateTimerRef.current = setTimeout(goToNextSlide, autoAnimateTimeMs);
    }
    
    // Cleanup function
    return () => {
      if (autoAnimateTimerRef.current) {
        clearTimeout(autoAnimateTimerRef.current);
      }
    };
  }, [currentIndex, autoAnimate, components.length, autoAnimateTimeMs, goToNextSlide]);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const handleDragStart = (event) => {
    if (autoAnimateTimerRef.current) {
      clearTimeout(autoAnimateTimerRef.current);
    }
    setDragStart(event.clientX);
  };

  const handleDragEnd = (event) => {
    const dragDistance = dragStart - event.clientX;
    const threshold = 50;

    if (dragDistance > threshold && currentIndex < components.length - 1) {
      setDirection(1);
      setCurrentIndex(currentIndex + 1);
    } else if (dragDistance < -threshold && currentIndex > 0) {
      setDirection(-1);
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleDotClick = (index) => {
    if (autoAnimateTimerRef.current) {
      clearTimeout(autoAnimateTimerRef.current);
    }
    setDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
  };

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? containerWidth : -containerWidth,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction) => ({
      x: direction > 0 ? -containerWidth : containerWidth,
      opacity: 0,
    }),
  };

  return (
    <div 
      ref={containerRef}
      style={{ position: 'relative', overflow: 'visible', height, width, maxWidth, marginLeft: 'auto', marginRight: 'auto' }}
    >
      <AnimatePresence initial={false} custom={direction}>
        <motion.div
          key={currentIndex}
          style={{
            position: 'absolute',
            width: '100%',
            height: 'calc(100% - 48px)',
            display: 'flex',
            justifyContent: 'center',
          }}
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        >
          {components[currentIndex]}
        </motion.div>
      </AnimatePresence>

      {/* Dots navigation */}
      <div
        style={{
          position: 'absolute',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: '8px',
        }}
      >
        {components.map((_, index) => {
          const isSelected = currentIndex === index;
          return (
            <button
              key={index}
              onClick={() => handleDotClick(index)}
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                border: 'none',
                background: isSelected ? '#000' : '#ccc',  // Used here
                cursor: 'pointer',
                padding: 0,
              }}
              aria-label={`Go to slide ${index + 1}`}
              aria-current={isSelected ? 'true' : 'false'}  // And here
            />
          );
        })}
      </div>
    </div>
  );
};

export default ComponentCarousel;